import React, { createContext, useState } from 'react';
import { SearchTableItem } from '../searchContainer/SearchTableItem';
import { Timeline } from '../activityTimeline/Timeline';
import Docrio from '../docrio/Docrio';
import { Time } from '../time/Time';
import Save from '../save/Save';
import { DeselectRecordModal } from './DeselectRecordModal';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, batch, shallowEqual } from 'react-redux';
import officeApi from '../../../api/officeApi';
import * as MailActions from '../../actions/mail';
import * as FileActions from '../../actions/files';
import * as TimeActions from '../../actions/time';
import * as RootActions from '../../actions/index';
import * as SalesforceRecordActions from '../../actions/salesforceRecords';
import { useInterval, useMountedState } from '../../utils/hooks';
import { isDesktop } from '../../utils/environment';
import { Accordion } from '../accordion/Accordion';
import { sortRecordsAlphabetically } from '../../utils/selectedRecordsUtils';
import SalesforceRecordButton from '../salesforceRecordButton/SalesforceRecordButton';

import './SelectedRecordContainer.scss';

export const SelectedRecordContainerContext = createContext({});

export const SelectedRecordContainer = ({ items, isObjectConfigured }) => {
  const dispatch = useDispatch();
  const mail = useSelector((state) => state.mail, shallowEqual);
  const ownerEmail = mail.ownerEmail;
  const ownerDisplayName = mail.ownerDisplayName;
  const { emails, draftId, isComposingEmail, emailSubject, emailBody } = mail;
  const isSaving = useSelector((state) => state.app.isLoading);
  const existingAttachments = useSelector((state) => state.files.docrioFiles);
  const timeEnabled = useSelector((state) => state.time.isTimeEnabled);
  const objectsAndDisplayFields = useSelector((state) => state.mail.settings?.objectsAndDisplayFields);
  const isMultiSave = useSelector((state) => state.mail.settings.multiSave);
  const additionalObjectsAndFieldSets = useSelector((state) => state.mail.settings?.additionalObjectsAndFieldSets);

  const [recordToDeselect, setRecordToDeselect] = useState();
  const [isSaveLocationExpanded, setIsSaveLocationExpanded] = useState(true);
  const isMounted = useMountedState();

  // If no items are present, set isSearchingRelatedRecords to true and redirect to search
  if (!items || Object.values(items)?.length == 0) {
    dispatch(MailActions.setIsSearchingRelatedRecords(true));
  }

  // check for changes in email subject and body
  useInterval(() => {
    emailChangedHandler();
  }, isComposingEmail ? 1000 : null);

  // detect changes in email subject and body
  const emailChangedHandler = async () => {
    if (isComposingEmail) {
      if (isMounted()) {
        const [newSubject, newBody] = await Promise.all([officeApi.getSubject(), officeApi.getBody()]);
        if (newSubject !== emailSubject) {
          const emailIds = new Set([emails?.itemId, draftId]);
          const files = existingAttachments.map((file) => emailIds.has(file.id) ? { ...file, name: newSubject } : file);
          if (isDesktop()) {
            await officeApi.saveEmail();
          }
          batch(() => {
            dispatch(MailActions.isEmailEdited(true));
            dispatch(MailActions.isSubjectEdited(true));
            dispatch(MailActions.setDocrioSubject(newSubject));
            dispatch(MailActions.setTimelineSubject(newSubject));
            dispatch(MailActions.setSubject(newSubject));
            dispatch(FileActions.setDocrioFiles(files));
          });
        }
        if (newBody !== emailBody) {
          batch(() => {
            dispatch(MailActions.isEmailEdited(true));
            dispatch(MailActions.setBody(newBody));
          });
        }
      }
    }
  };

  // handle salesforce action click
  const handleSalesforceRecordClick = (action) => {
    // set the action
    dispatch(SalesforceRecordActions.setSalesforceRecordObjectApiName(action));
    // clear current actions to prevent multiple actions from being set
    dispatch(MailActions.setIsSearchingRelatedRecords(false));
    dispatch(SalesforceRecordActions.setIsSalesforceRecordFormOpen(true));
    dispatch(TimeActions.addingTime(false));
    dispatch(RootActions.isLoading(true));
  };

  // Add saved location(s) link
  const addSavedLocationsSpan = (
    <span
      className='addSavedLocationsSpan'
      onClick={() => {
        dispatch(MailActions.setIsSearchingRelatedRecords(true));
      }}
    >Add saved location(s)</span>
  );

  return (<>
    <div className={`selectedRecordContainer ${isSaving && 'selectedRecordContainer-deactivated'}`}>
      <SelectedRecordContainerContext.Provider value={{
        emailId: emails?.itemId || draftId,
        isComposingEmail,
      }}>
        {ownerEmail ?
          <p className='status-sharedUserBanner'>Viewing as {ownerDisplayName || ownerEmail}</p> : <></>
        }
        { additionalObjectsAndFieldSets?.length > 0 &&
            <div className='salesforce-records'>
              { additionalObjectsAndFieldSets.map((object) => (
                <SalesforceRecordButton key={object.objectApiName} objectLabel={`Add ${object.objectLabel}`} objectApiName={object.objectApiName} objectOnClick={() => handleSalesforceRecordClick(object.objectApiName)} />
              ))}
            </div>
        }
        <Accordion title='Saved Location' status={isSaveLocationExpanded} link={isMultiSave ? addSavedLocationsSpan : ''} setStatus={setIsSaveLocationExpanded}/>
        { isSaveLocationExpanded &&
          (
            (items && Object.values(items)?.length > 0) &&
              sortRecordsAlphabetically(items, objectsAndDisplayFields)?.map((item) => (
                <SearchTableItem key={item.id} item={item} isSelectedRecord={true} setRecordToDeselect={setRecordToDeselect}/>
              ))
          )
        }
        <Timeline />
        <Docrio isObjectConfigured={isObjectConfigured}/>
        {timeEnabled && <Time />}
        <Save />
        <DeselectRecordModal recordToDeselect={recordToDeselect} setRecordToDeselect={setRecordToDeselect}/>
      </SelectedRecordContainerContext.Provider>
    </div>
  </>);
};


SelectedRecordContainer.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  isObjectConfigured: PropTypes.bool,
};
